import { MoodleInstanceKey } from './slices/moodleSlice';

/**
 * Canonical link builders for Moodle event and course level detail pages.
 *
 * These exist because the same two destinations are linked from several tables
 * (workshops upcoming/previous events, leader team events, TTT proctors, user
 * course progress) and the URL shapes are easy to get wrong:
 *
 *  - event level  -> WorkshopsRoutes -> EventSummaryPage
 *  - course level -> ROUTES.learningCourse -> LearningCourseRoute
 */

export const EVENT_SUMMARY_ROUTE_BASE = '/workshops/event-summary';

export type EventRouteBase =
  | '/workshops/upcoming-events'
  | '/workshops/previous-events'
  | '/workshops/event-summary';

type CourseDetailOptions = {
  /** Path to return to when the user exits the course. */
  returnTo?: string;
  /** Deep link straight to a single activity within the course. */
  itemId?: number | string;
  /** External = workshops content, internal = cyber academy. */
  instance?: MoodleInstanceKey;
  /** Render the course without the app shell. Defaults to true. */
  fullscreen?: boolean;
};

function isUsableId(value: unknown): boolean {
  if (value === null || value === undefined || value === '') return false;
  return Number.isFinite(Number(value));
}

/**
 * Event level details (registrants, completion, feedback) for one event.
 * Returns undefined when either id is missing so callers can render plain text
 * instead of a link that goes nowhere.
 */
export function buildEventDetailPath(
  courseId: number | string | undefined | null,
  eventId: number | string | undefined | null,
  returnTo?: string,
  routeBase: EventRouteBase = EVENT_SUMMARY_ROUTE_BASE
): string | undefined {
  if (!isUsableId(courseId) || !isUsableId(eventId)) return undefined;

  const path = `${routeBase}/${Number(courseId)}/${Number(eventId)}`;
  return returnTo ? `${path}?returnTo=${encodeURIComponent(returnTo)}` : path;
}

/**
 * Course level details (course content / activities) for one course.
 * Returns undefined when the course id is missing.
 */
export function buildCourseDetailPath(
  courseId: number | string | undefined | null,
  options: CourseDetailOptions = {}
): string | undefined {
  if (!isUsableId(courseId)) return undefined;

  const { returnTo, itemId, instance = 'external', fullscreen = true } = options;

  const params = new URLSearchParams();
  if (fullscreen) params.set('fullscreen', '1');
  if (returnTo) params.set('returnTo', returnTo);
  if (isUsableId(itemId)) params.set('itemId', String(Number(itemId)));

  const query = params.toString();
  const path = `/learning/moodle/${instance}/course/${Number(courseId)}`;
  return query ? `${path}?${query}` : path;
}
